/**
 * WebSocket client for our own backend.
 *
 * This is what replaces Firestore onSnapshot after the migration. The crucial
 * part is that it must expose the SAME subscribe/unsubscribe shape the
 * contracts require, so no component can tell the two apart:
 *
 *     const unsub = messages.subscribe(chatId, cb)   // identical either way
 *
 * The server pushes a flat frame — { topic, action, ...data } — and decides
 * who receives it from chat membership. The client does not ask to be
 * subscribed; it only routes what arrives by topic. The subscribe and
 * unsubscribe frames below are sent for a server that may one day want them
 * and are ignored by the current one.
 */

import { getAccessToken } from './httpClient.js'
import { getConfig } from '../../config.js'

let socket = null
const topicListeners = new Map()
let reconnectDelay = 1000

export function connect() {
  if (socket && socket.readyState <= WebSocket.OPEN) return socket

  const wsUrl = getConfig().wsUrl
  socket = new WebSocket(`${wsUrl}?token=${encodeURIComponent(getAccessToken() ?? '')}`)

  socket.addEventListener('open', () => {
    reconnectDelay = 1000
    // Re-subscribe after a reconnect, or the UI silently goes stale.
    for (const topic of topicListeners.keys()) {
      send({ action: 'subscribe', topic })
    }
  })

  socket.addEventListener('message', event => {
    let frame
    try { frame = JSON.parse(event.data) } catch { return }
    // The whole frame, not frame.payload: the server puts its data alongside
    // the topic rather than nested under it, so reading .payload handed every
    // listener undefined.
    for (const listener of topicListeners.get(frame.topic) ?? []) {
      listener(frame)
    }
  })

  socket.addEventListener('close', () => {
    // Exponential backoff, capped, so a server restart does not cause a
    // reconnect storm from every open client at once.
    setTimeout(connect, reconnectDelay)
    reconnectDelay = Math.min(reconnectDelay * 2, 30_000)
  })

  return socket
}

export function send(frame) {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(frame))
}

/**
 * Subscribe to a server topic.
 * @returns {() => void} unsubscribe
 */
export function subscribe(topic, callback) {
  connect()
  if (!topicListeners.has(topic)) {
    topicListeners.set(topic, new Set())
    send({ action: 'subscribe', topic })
  }
  topicListeners.get(topic).add(callback)

  return () => {
    const listeners = topicListeners.get(topic)
    if (!listeners) return
    listeners.delete(callback)
    if (!listeners.size) {
      topicListeners.delete(topic)
      send({ action: 'unsubscribe', topic })
    }
  }
}

export function disconnect() {
  topicListeners.clear()
  socket?.close()
  socket = null
}
